import React, { useEffect, useMemo, useState } from "react";
import { MapContainer, TileLayer, Marker, useMap, LayersControl } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { api, API_BASE, fmtDate, mediaUrl } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useT } from "@/lib/i18n";
import { Camera, MapPin, X, ExternalLink, Trash2, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

// Fix default Leaflet marker icons path (webpack breaks them otherwise)
import iconUrl from "leaflet/dist/images/marker-icon.png";
import iconRetinaUrl from "leaflet/dist/images/marker-icon-2x.png";
import shadowUrl from "leaflet/dist/images/marker-shadow.png";
L.Icon.Default.mergeOptions({ iconUrl, iconRetinaUrl, shadowUrl });

function FitBounds({ points }) {
  const map = useMap();
  useEffect(() => {
    if (!points.length) return;
    const bounds = L.latLngBounds(points.map(p => [p.latitude, p.longitude]));
    map.fitBounds(bounds, { padding: [40, 40], maxZoom: 13 });
  }, [points, map]);
  return null;
}

function thumbIcon(fileId) {
  const src = mediaUrl(fileId);
  const html = `
    <div class="photo-pin" style="width:56px;height:56px;border-radius:8px;overflow:hidden;border:2px solid #22D3EE;box-shadow:0 4px 12px rgba(0,0,0,0.6);background:#0B1221;">
      <img src="${src}" style="width:100%;height:100%;object-fit:cover;display:block;" alt="" />
    </div>`;
  return L.divIcon({ html, className: "photo-marker", iconSize: [56, 56], iconAnchor: [28, 28] });
}

export default function PhotoMap() {
  const t = useT();
  const { user } = useAuth();
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const canDeleteAny = ["super_admin", "owner_director", "operations_admin"].includes(user?.role);
  const canDelete = (m) => canDeleteAny || m?.uploaded_by_user_id === user?.id;

  const onDelete = async (m) => {
    if (!window.confirm(t("map.sheet.deleteConfirm"))) return;
    try {
      await api.delete(`/media/${m.id}`);
      setMedia(arr => arr.filter(x => x.id !== m.id));
      setSelected(null);
      toast.success(t("map.sheet.deleted"));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/media/map");
        setMedia(data);
      } catch (e) { console.error(e); }
      setLoading(false);
    })();
  }, []);

  // Default center: average of points, or Piran if none
  const center = useMemo(() => {
    if (!media.length) return [45.5270, 13.5685]; // Piran, Slovenia
    const lat = media.reduce((s, m) => s + m.latitude, 0) / media.length;
    const lng = media.reduce((s, m) => s + m.longitude, 0) / media.length;
    return [lat, lng];
  }, [media]);

  return (
    <div className="space-y-4" data-testid="photo-map-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="font-mono text-[10px] tracking-widest text-[#94A3B8]">{t("map.subtitle")}</div>
          <h2 className="font-display text-2xl font-bold tracking-tight">{t("map.title")}</h2>
          <p className="text-sm text-[#94A3B8] mt-1">
            {loading ? t("map.loading") : t("map.status", { count: media.length })}
          </p>
        </div>
      </div>

      <div className="ledger-card overflow-hidden relative" style={{ height: "70vh" }} data-testid="photo-map-container">
        {loading ? (
          <div className="w-full h-full flex items-center justify-center font-mono text-xs text-[#94A3B8]">{t("map.loadingTiles")}</div>
        ) : (
          <>
            <MapContainer center={center} zoom={media.length ? 11 : 9} style={{ height: "100%", width: "100%", background: "#0B1221" }} scrollWheelZoom>
              <LayersControl position="topright">
                <LayersControl.BaseLayer checked name={t("map.layer.map")}>
                  <TileLayer
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    attribution='&copy; OpenStreetMap contributors'
                    maxZoom={19}
                  />
                </LayersControl.BaseLayer>
                <LayersControl.BaseLayer name={t("map.layer.satellite")}>
                  <TileLayer
                    url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                    attribution='Tiles &copy; Esri — Source: Esri, Maxar, Earthstar Geographics'
                    maxZoom={19}
                  />
                </LayersControl.BaseLayer>
                <LayersControl.BaseLayer name={t("map.layer.terrain")}>
                  <TileLayer
                    url="https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png"
                    attribution='&copy; OpenTopoMap (CC-BY-SA)'
                    maxZoom={17}
                  />
                </LayersControl.BaseLayer>
                <LayersControl.BaseLayer name={t("map.layer.nautical")}>
                  <TileLayer
                    url="https://tiles.openseamap.org/seamark/{z}/{x}/{y}.png"
                    attribution='&copy; OpenSeaMap contributors'
                    maxZoom={18}
                  />
                </LayersControl.BaseLayer>
              </LayersControl>
              {media.length > 0 && <FitBounds points={media} />}
              {media.map(m => (
                <Marker key={m.id} position={[m.latitude, m.longitude]} icon={thumbIcon(m.id)}
                  eventHandlers={{ click: () => setSelected(m) }}
                />
              ))}
            </MapContainer>
            {media.length === 0 && (
              <div className="pointer-events-none absolute inset-x-0 top-4 z-[500] flex justify-center px-4" data-testid="photo-map-empty-overlay">
                <div className="pointer-events-auto bg-[#050A14]/90 border border-[#1E293B] rounded-md px-4 py-2 flex items-center gap-2 text-xs text-[#94A3B8] backdrop-blur-sm">
                  <Camera className="w-4 h-4 text-[#4DA6FF]" />
                  <div>
                    <div className="text-[#F8FAFC]">{t("map.empty")}</div>
                    <div className="text-[10px] text-[#94A3B8]">{t("map.emptyHint")}</div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {selected && (
        <PhotoSheet
          media={selected}
          onClose={() => setSelected(null)}
          onDelete={canDelete(selected) ? () => onDelete(selected) : null}
          t={t}
        />
      )}
    </div>
  );
}

function PhotoSheet({ media, onClose, onDelete, t }) {
  const [err, setErr] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const handleDelete = async () => {
    if (!onDelete || deleting) return;
    setDeleting(true);
    try { await onDelete(); }
    finally { setDeleting(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80" onClick={onClose} data-testid="photo-sheet">
      <div className="ledger-card max-w-3xl w-full max-h-[90vh] overflow-auto relative" onClick={(e) => e.stopPropagation()}>
        {/* Floating action bar (always visible above the image).
            On mobile the button label stays visible too — a tiny icon-only pill
            was getting missed at phone zoom levels. */}
        <div className="absolute top-3 right-3 z-10 flex items-center gap-2">
          {onDelete && (
            <button
              onClick={handleDelete}
              disabled={deleting}
              data-testid="photo-sheet-delete-btn"
              title={t("map.sheet.deletePhoto")}
              className="flex items-center gap-1.5 px-4 py-2.5 rounded-md bg-[#EF4444] hover:bg-[#DC2626] text-white text-sm font-semibold shadow-lg border-2 border-white/20 disabled:opacity-50"
            >
              {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
              <span>{deleting ? t("common.saving") : t("map.sheet.deletePhoto")}</span>
            </button>
          )}
          <button
            onClick={onClose}
            className="p-2.5 rounded-md bg-[#050A14]/90 hover:bg-[#131C31] text-[#F8FAFC] shadow-lg border-2 border-white/20"
            data-testid="photo-sheet-close"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="bg-[#050A14] flex items-center justify-center min-h-[300px]">
          {err ? (
            <div className="text-sm text-[#F87171] p-8">{t("map.sheet.loadFail")}</div>
          ) : (
            <img
              src={mediaUrl(media.id)}
              alt={media.caption || ""}
              className="max-w-full max-h-[65vh] object-contain"
              onError={() => setErr(true)}
            />
          )}
        </div>
        <div className="p-5 space-y-3">
          <div className="flex items-start justify-between flex-wrap gap-2">
            <div>
              <div className="font-display text-lg font-bold tracking-tight">{media.log_place || media.caption || "Untitled photo"}</div>
              <div className="font-mono text-[11px] text-[#94A3B8] mt-1 flex items-center gap-2 flex-wrap">
                <MapPin className="w-3 h-3" />
                {media.latitude.toFixed(5)}, {media.longitude.toFixed(5)}
                {media.captured_at && <span>· {t("map.sheet.captured")} {fmtDate(media.captured_at)}</span>}
                {media.log_date && !media.captured_at && <span>· {fmtDate(media.log_date)}</span>}
              </div>
            </div>
            {media.linked_type === "daily_log" && media.linked_id && (
              <Link to="/app/journal" className="text-xs text-[#4DA6FF] hover:underline flex items-center gap-1" data-testid="photo-sheet-journal-link">
                {t("map.sheet.viewJournal")} <ExternalLink className="w-3 h-3" />
              </Link>
            )}
          </div>
          {media.caption && <div className="text-sm text-[#F8FAFC]">{media.caption}</div>}
        </div>
      </div>
    </div>
  );
}
